import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, Repeat } from "lucide-react";
import { toast } from "sonner";

const RECURRENCE_OPTIONS = [
  { value: "0", label: "One-time" },
  { value: "1", label: "Every day" },
  { value: "2", label: "Every 2 days" },
  { value: "3", label: "Every 3 days" },
  { value: "7", label: "Every week" },
  { value: "14", label: "Every 2 weeks" },
  { value: "30", label: "Every month" },
];

export default function TaskList({ scope = "daily", title }) {
  const [tasks, setTasks] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [recurrence, setRecurrence] = useState("0");
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tasks?scope=${scope}`);
      setTasks(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [scope]);

  const add = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    const payload = { title: newTitle.trim(), scope };
    const days = parseInt(recurrence, 10);
    if (days > 0) payload.recurrence_days = days;
    const r = await api.post("/tasks", payload);
    setTasks((t) => [r.data, ...t]);
    setNewTitle("");
    setRecurrence("0");
  };

  const toggle = async (t) => {
    const r = await api.patch(`/tasks/${t.id}`, { done: !t.done });
    setTasks((arr) => arr.map((x) => (x.id === t.id ? r.data : x)));
    if (!t.done && t.recurrence_days) {
      toast.success(`Next ${t.title} scheduled.`);
      // Pull in the freshly created next instance
      refresh();
    }
  };

  const remove = async (t) => {
    await api.delete(`/tasks/${t.id}`);
    setTasks((arr) => arr.filter((x) => x.id !== t.id));
    toast.message("Task removed.");
  };

  return (
    <div className="sage-card p-6" data-testid={`tasklist-${scope}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-overline">{scope}</div>
          <h3 className="font-serif text-2xl font-light">{title || "Tasks"}</h3>
        </div>
        <span className="text-xs text-muted-foreground" data-testid={`task-count-${scope}`}>
          {tasks.filter((t) => !t.done).length} open
        </span>
      </div>

      <form onSubmit={add} className="flex flex-col sm:flex-row gap-2 mb-4">
        <Input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder={`Add a ${scope} task…`}
          data-testid={`task-input-${scope}`}
          className="flex-1"
        />
        <Select value={recurrence} onValueChange={setRecurrence}>
          <SelectTrigger className="sm:w-44" data-testid={`task-recurrence-${scope}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RECURRENCE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="submit" size="icon" className="bg-primary hover:bg-primary/90" data-testid={`add-task-${scope}`}>
          <Plus className="w-4 h-4" />
        </Button>
      </form>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : tasks.length === 0 ? (
        <div className="text-sm text-muted-foreground italic">Nothing planted here yet.</div>
      ) : (
        <ul className="divide-y divide-border">
          {tasks.map((t) => (
            <li key={t.id} className="flex items-center gap-3 py-2.5 group" data-testid={`task-${t.id}`}>
              <Checkbox
                checked={t.done}
                onCheckedChange={() => toggle(t)}
                className="border-primary/60 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                data-testid={`task-check-${t.id}`}
              />
              <span className={`flex-1 text-sm ${t.done ? "line-through text-muted-foreground" : ""}`}>
                {t.title}
              </span>
              {t.recurrence_days ? (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-accent-foreground bg-accent/30 px-2 py-0.5 rounded" title={`Every ${t.recurrence_days} day(s)`}>
                  <Repeat className="w-3 h-3" /> {t.recurrence_days}d
                </span>
              ) : null}
              <button
                onClick={() => remove(t)}
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                data-testid={`task-delete-${t.id}`}
                aria-label="Delete task"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
